/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { IPartner, IPlatform } from 'pages/PayCms/MasterTables/utils/interface'
import { IPlan } from 'pages/PayCms/Plans/Utils/constants'
import { get } from 'utils/apiClient'
import { IApiResponse } from 'utils/apiInterfaces'
import apiEndPoints from 'utils/constants/apiEndpoints'

interface ICountry {
  countryName: string
  countryCode: string
  code: number
  countryId: number
  modifiedDate: string
  status: string
}

interface ICountryList {
  countryName: string
  countryCode: string
  code: number
  countryId: number
  modifiedDate: string
  status: string
}
interface IProduct {
  productId: number
  productName: string
  productCode: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface ICommonData {
  countries: { label: string; value: number }[]
  countryList: ICountryList[]
  platforms: { label: string; value: string | number }[]
  products: { label: string; value: string | number }[]
  plans: { label: string; value: string | number; id: string }[]
  partners: IPartner[]
}

export const fetchCountryListByCode = createAsyncThunk(
  'auth/fetchCountryList',
  async () => {
    const countriesData: IApiResponse<ICountry[]> = await get(
      `${apiEndPoints.COUNTRY_LIST}?page=1&limit=1000`
    )
    return countriesData.data
  }
)

export const fetchPlatformList = createAsyncThunk(
  'auth/fetchPlatformList',
  async () => {
    const platformData: IApiResponse<IPlatform[]> = await get(
      `${apiEndPoints.PLATFORM_LIST}?page=1&limit=1000`
    )
    return platformData.data
  }
)
export const fetchProductList = createAsyncThunk(
  'auth/fetchProductsList',
  async () => {
    const productData: IApiResponse<IProduct[]> = await get(
      `${apiEndPoints.PRODUCT_LIST}?page=1&limit=1000`
    )
    return productData.data
  }
)

export const fetchPlansList = createAsyncThunk(
  'auth/fetchPlansData',
  async () => {
    const plansData: IApiResponse<IPlan[]> = await get(
      `${apiEndPoints.COUPON_PLANS}?pagination=false&vodType=svod`
    )
    return plansData.data
  }
)

export const fetchPartners = createAsyncThunk(
  'auth/fetchPartners',
  async () => {
    const partnersData: IApiResponse<IPartner[]> = await get(
      `${apiEndPoints.PARTNERS_LIST}?pagination=false&isActive=true`
    )
    return partnersData.data
  }
)

const initialState: ICommonData = {
  countries: [],
  countryList: [],
  platforms: [],
  products: [],
  plans: [],
  partners: [],
}

const regionDataSlice = createSlice({
  name: 'regionData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchCountryListByCode.pending, (state) => {
        state.countries = []
      })
      .addCase(
        fetchCountryListByCode.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.countryList = action.payload
          state.countries = action.payload.map((country: ICountry) => ({
            label: country.countryName,
            value: country.countryCode,
          }))
        }
      )
      .addCase(fetchCountryListByCode.rejected, (state) => {
        state.countries = []
        state.countryList = []
      })
      .addCase(fetchPlatformList.pending, (state) => {
        state.platforms = []
      })
      .addCase(
        fetchPlatformList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.platforms = action.payload.map((platform: IPlatform) => ({
            label: platform.platformName,
            value: platform.platformCode,
          }))
        }
      )
      .addCase(fetchPlatformList.rejected, (state) => {
        state.platforms = []
      })
      .addCase(fetchProductList.pending, (state) => {
        state.products = []
      })
      .addCase(
        fetchProductList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.products = action.payload.map((product: IProduct) => ({
            label: product.productName,
            value: product.productCode,
          }))
        }
      )
      .addCase(fetchProductList.rejected, (state) => {
        state.products = []
      })
      .addCase(fetchPlansList.pending, (state) => {
        state.plans = []
      })
      .addCase(
        fetchPlansList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.plans = action.payload.map((plan: IPlan) => ({
            label: plan?.title,
            value: plan?.planMasterId,
            id: plan?.id,
            axinomPlanId: plan?.axinomPlanId,
          }))
        }
      )
      .addCase(fetchPlansList.rejected, (state) => {
        state.plans = []
      })
      .addCase(fetchPartners.pending, (state) => {
        state.partners = []
      })
      .addCase(fetchPartners.fulfilled, (state, action: PayloadAction<any>) => {
        state.partners = action.payload.map((partner: IPartner) => ({
          label: partner.name,
          value: partner.name,
        }))
      })
      .addCase(fetchPartners.rejected, (state) => {
        state.partners = []
      })
  },
})

export default regionDataSlice.reducer
